import { useState, useEffect } from "react";
import { getAdById, apiAdToAd, getCategories } from "@/app/api/auth-client";
import type { Ad } from "@/app/components/ad-card";
import { enrichAdCategoryFromCatalog } from "@/app/utils/ad-utils";

/**
 * Fetches full ad by id for the ad-detail page.
 * Pass adId only when currentPage === "ad-detail" so we don't fetch unnecessarily.
 */
export function useAdDetail(adId: string | null): {
  ad: Ad | null;
  loading: boolean;
  error: Error | null;
} {
  const [ad, setAd] = useState<Ad | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!adId) {
      setAd(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAdById(adId)
      .then(async (api) => {
        const base = apiAdToAd(api);
        const catalog = await getCategories().catch(() => []);
        if (!cancelled) {
          setAd(enrichAdCategoryFromCatalog(base, catalog));
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
          setAd(null);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adId]);

  return { ad, loading, error };
}
